import React, { Fragment, useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { get } from 'lodash-es';
import Container from 'reactstrap/lib/Container';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { BuildPriceEntities, BuildPriceModel, BuildPricePaths } from 'client/data/models/build-price';
import { TrackingHandler } from 'client/tracking/handler';
import { TrackingMap } from 'client/tracking/maps/build-price/build-price';
import { useToggle } from 'site-modules/shared/hooks/use-toggle';
import { isRenderedAdUnit } from 'client/utils/ads';
import {
  buildBreadcrumbs,
  getAdsCustomTargeting,
  getModelYearById,
} from 'site-modules/shared/utils/build-price/build-price';
import { getLeadFormCreativeId } from 'site-modules/shared/components/lead-form/utils/lead-form-creative-id';
import { LEAD_FORMS_CREATIVE_ID } from 'site-modules/shared/components/lead-form/unified-lead-form/constants/creative-ids';
import { STEP_NUMBER } from 'site-modules/shared/constants/build-price';
import { BUILD_PRICE_SMILEY_SURVEY_CONFIG } from 'site-modules/build-price/constants/build-price';
import { BuildPriceAd } from 'site-modules/shared/components/native-ad/buildprice-ad/buildprice-ad';
import { Breadcrumbs } from 'site-modules/shared/components/breadcrumbs/breadcrumbs';
import { SponsoredCars } from 'site-modules/shared/components/core-page/sponsored-cars/sponsored-cars';
import { BuildPriceStepsWrapper } from 'site-modules/build-price/components/build-price-steps-wrapper/build-price-steps-wrapper';
import { BuildPriceLeadForm } from 'site-modules/build-price/components/build-price-lead-form/build-price-lead-form';
import { BuildStepsMenu } from 'site-modules/build-price/components/build-steps-menu/build-steps-menu';
import { RelatedBuildsLinks } from 'site-modules/build-price/components/related-builds-links/related-builds-links';
import { MinHeightWrapper } from 'site-modules/shared/components/min-height-wrapper/min-height-wrapper';
import { SmileySurvey } from 'site-modules/shared/components/smiley-survey/smiley-survey';
import { Experiment, Recipe } from 'site-modules/shared/components/experiment';

export function BuildPriceUI({
  params,
  isMobile = false,
  modelYears = null,
  buildPriceFilter = null,
  pathname = '',
  pageName = '',
}) {
  const [isSummaryLeadFormSubmitted, setSummaryLeadFormSubmitted] = useToggle(false, { limit: 1 });
  const [isBuildPriceAdVisible, setBuildPriceAdVisible] = useState(true);
  const step = get(buildPriceFilter, 'step');
  const styleId = get(buildPriceFilter, 'styleId');

  useEffect(() => {
    TrackingHandler.useMap(TrackingMap);
  }, []);

  const hideEmptyAdContainer = useCallback(slot => {
    setBuildPriceAdVisible(isRenderedAdUnit(slot));
  }, []);

  if (!(modelYears && step)) {
    return (
      <main id="main-content" tabIndex={-1} className="build-price">
        <Container>Loading&hellip;</Container>
      </main>
    );
  }

  const modelYear = getModelYearById(modelYears, get(buildPriceFilter, 'styleFilter.modelyearId'));
  const { makeName, modelName, makeSlug, modelSlug, year } = modelYear;

  const dataTracking = getLeadFormCreativeId({
    isNewVin: true,
    pageNameLegacy: pageName,
    formName: LEAD_FORMS_CREATIVE_ID.FOOTER_EMBEDDED_FORM,
  });
  const dataTrackingStep4 = getLeadFormCreativeId({
    isNewVin: true,
    pageNameLegacy: pageName,
    formName: LEAD_FORMS_CREATIVE_ID.FOOTER_EMBEDDED_FORM_STEP_4,
  });

  return (
    <main id="main-content" tabIndex={-1} role="main" className="build-price">
      <Container>
        <Breadcrumbs data={buildBreadcrumbs(modelYear)} className="px-0" />
        <h1 className="heading-3 mb-1_5 mb-md-2">
          {makeName} {modelName} Build & Price
        </h1>
        <BuildStepsMenu
          buildPriceFilter={buildPriceFilter}
          makeSlug={makeSlug}
          modelSlug={modelSlug}
          year={year}
          pathname={pathname}
          isSummaryLeadFormSubmitted={isSummaryLeadFormSubmitted}
          isMobile={isMobile}
        />
        <BuildPriceStepsWrapper
          params={params}
          modelYears={modelYears}
          isMobile={isMobile}
          buildPriceFilter={buildPriceFilter}
          pathname={pathname}
          isSummaryLeadFormSubmitted={isSummaryLeadFormSubmitted}
        />
        {step === STEP_NUMBER.STEP_5 ? (
          <div className="get-quotes-lead-form-wrapper mb-3_5">
            <MinHeightWrapper componentType="TRANSPARENT_PRICING_LEAD_FORM">
              <BuildPriceLeadForm
                buildPriceFilter={buildPriceFilter}
                modelYear={modelYear}
                styleId={styleId}
                options={{
                  dataTrackingParent: dataTrackingStep4,
                  creativeId: dataTrackingStep4,
                  headerText: 'Get competitive price quotes',
                  isMobile,
                }}
                isTransparentPricing
              />
            </MinHeightWrapper>
            <SmileySurvey className="mt-2" cardClassName="px-1_25" {...BUILD_PRICE_SMILEY_SURVEY_CONFIG} />
          </div>
        ) : (
          <Fragment>
            {step === STEP_NUMBER.STEP_4 && (
              <Fragment>
                <hr className="my-3" />
                <MinHeightWrapper componentType="UNIFIED_LEAD_FORM">
                  <BuildPriceLeadForm
                    buildPriceFilter={buildPriceFilter}
                    modelYear={modelYear}
                    styleId={styleId}
                    options={{
                      dataTrackingParent: dataTracking,
                      creativeId: dataTracking,
                    }}
                    onSummaryLeadFormSubmit={setSummaryLeadFormSubmitted}
                  />
                </MinHeightWrapper>
                {isMobile && <SmileySurvey className="mt-2" {...BUILD_PRICE_SMILEY_SURVEY_CONFIG} />}
                {isBuildPriceAdVisible && <hr className="build-price-separator my-3" />}
                <BuildPriceAd
                  mobile={isMobile}
                  wrapperClass="d-flex justify-content-center"
                  slotRenderEndListener={hideEmptyAdContainer}
                  customTargeting={getAdsCustomTargeting(buildPriceFilter.step)}
                />
              </Fragment>
            )}
            {isMobile && step !== STEP_NUMBER.STEP_4 && (
              <SmileySurvey className="mt-2" {...BUILD_PRICE_SMILEY_SURVEY_CONFIG} />
            )}
            {(!isMobile || step === STEP_NUMBER.STEP_4) && (
              <Fragment>
                <hr className="mt-2 mb-3" />
                <Experiment name="ads-12460_dfp" showDefault>
                  <Recipe name="ctrl" isDefault>
                    <MinHeightWrapper componentType="SPONSORED_CARS">
                      <SponsoredCars
                        showBottomDividingLine={false}
                        adsCustomTargeting={getAdsCustomTargeting(buildPriceFilter.step)}
                      />
                    </MinHeightWrapper>
                  </Recipe>
                  <Recipe name="chal">
                    <MinHeightWrapper
                      componentType="SPONSORED_CARS_STACKED"
                      style={isMobile ? null : { maxWidth: '744px' }}
                    >
                      <SponsoredCars
                        showBottomDividingLine={false}
                        adsCustomTargeting={getAdsCustomTargeting(buildPriceFilter.step)}
                        showStackedAds
                      />
                    </MinHeightWrapper>
                  </Recipe>
                </Experiment>
              </Fragment>
            )}
          </Fragment>
        )}
        <hr className="mt-2 mb-1_5" />
        <RelatedBuildsLinks modelYear={modelYear} />
      </Container>
    </main>
  );
}

BuildPriceUI.propTypes = {
  params: PropTypes.shape({
    make: PropTypes.string,
    model: PropTypes.string,
  }).isRequired,
  pathname: PropTypes.string,
  modelYears: PropTypes.arrayOf(BuildPriceEntities.ModelYear),
  buildPriceFilter: BuildPriceEntities.BuildPriceFilter,
  isMobile: PropTypes.bool,
  pageName: PropTypes.string,
};

const mapStateToProps = state => ({
  isMobile: state.mobile,
  pathname: get(state, 'pageContext.location.pathname', ''),
  pageName: get(state, 'pageContext.legacy.pageName') || get(state, 'pageContext.page.name'),
});

export const BuildPrice = connect(mapStateToProps)(
  connectToModel(BuildPriceUI, {
    modelYears: bindToPath(({ params }) => BuildPricePaths.buildModelYearsPath(params), BuildPriceModel),
    buildPriceFilter: bindToPath(BuildPricePaths.getBuildPriceFilterPath, BuildPriceModel),
  })
);
